<template>
  <span class="ed-help-wrap">
    <b-button
      size="sm"
      variant="outline-secondary"
      title="How to use the ERM Dashboard"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Using the ERM Dashboard"
      size="lg"
      ok-only
      scrollable
    >
      <div class="ed-help-body">
        <h4>What this page is</h4>
        <p>
          The same events as the <b>Event Log</b>, counted and charted. The log is for
          reading one event; this page answers &ldquo;how much, of what kind, and which way
          is it going?&rdquo;
        </p>

        <hr/>

        <h4>The range</h4>
        <ul>
          <li>
            <b>30d</b>, <b>90d</b>, and <b>12mo</b> set the window, counted by when each
            event was filed. Everything on the page follows it.
          </li>
          <li>
            Under <b>Include</b>, the <b>archived</b> chip adds events whose category a
            fleet admin has turned off to every tile and chart on the page. It is off by
            default, and separate from the Event Log's chip.
          </li>
          <li>
            The tiles compare against the window of the same length just before this one:
            <b>vs prior 30 days</b> for the 30d range, and so on.
          </li>
          <li>
            <b>Resolved</b> deliberately has no comparison. The earlier window has had more
            time to close its events, so a healthy fleet would always read as a decline.
            <b>Open now</b> is a snapshot, so it has none either.
          </li>
          <li>
            When a range holds more events than one pass reads, a note above the tiles says
            how many were counted.
          </li>
        </ul>

        <hr/>

        <h4>The tiles</h4>
        <ul>
          <li><b>Events</b>: everything filed in the range.</li>
          <li><b>Open now</b>: events from the range that are not yet resolved.</li>
          <li><b>Resolved</b>: events from the range that have been closed out.</li>
          <li><b>Kudos</b>: recognition filed in the range.</li>
          <li><b>Customer delayed</b>: issues where the customer was delayed.</li>
          <li><b>Events / week</b>: the range's total as a weekly rate.</li>
          <li>
            <b>Issues / 100k mi</b> reads &ldquo;Coming soon&rdquo;: it needs a mileage
            figure from your telematics and isn't populated yet.
          </li>
        </ul>

        <hr/>

        <h4>Events over time</h4>
        <ul>
          <li>
            Each column is a day (or a week, on the longer ranges), stacked by type:
            issues, kudos, and notes. The line is a trailing average that smooths the daily
            spikes so the direction shows through.
          </li>
          <li>
            Hover a column for its exact numbers, or click into the chart and use the left
            and right arrow keys. <b>Table</b> shows the same numbers as rows.
          </li>
        </ul>

        <hr/>

        <h4>Rankings and mixes</h4>
        <ul>
          <li>
            <b>Events by category</b>, <b>by driver</b>, and <b>by vehicle</b> rank the
            busiest of each. The bar is relative to the top row and the count is always
            printed beside it. The driver card has an <b>Issues</b> / <b>Kudos</b> lens, and
            each lens is ranked on its own, so a driver who leads on kudos is never hidden
            behind the by-total cut.
          </li>
          <li>
            Rankings key on the driver and vehicle as they were typed on each event, so a name
            entered two ways ranks as two rows. Picking from the roster suggestions when you
            file an entry keeps them together.
          </li>
          <li>
            The bands at the bottom show the mix of <b>Status</b>, <b>Severity</b>,
            <b>Customer delayed</b>, and <b>Submissions by source</b> (staff, imported, or
            the public form), each with a count and a share. Status and severity show each
            event's current state.
          </li>
          <li>
            Tech Support and fleet admins also see a <b>Preventability</b> card, built from
            the classification set on each event's coaching card.
          </li>
        </ul>

        <hr/>

        <h4>Where to go next</h4>
        <p>
          The <b>Event Log</b> is the record behind every number here, with filters for the
          same status, type, severity, driver, and vehicle. The <b>Event Board</b> shows the
          open work as lanes.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
// how-to modal for the ERM Dashboard (the analytics view), following the
// erm_board_help / driver_manager_*_help house pattern (info-icon button +
// ok-only scrollable modal). the dashboard's own chrome explains nothing
// about the comparison windows or the missing resolved delta, so this is
// where those get said.
export default {
  name: 'erm_dashboard_help',
  data() {
    return { show_help: false }
  },
}
</script>

<style scoped>
.ed-help-body h4 { font-size: 1.05rem; margin-top: 4px; }
.ed-help-body p, .ed-help-body li { font-size: 0.95rem; }
.ed-help-body li { margin-bottom: 6px; }
</style>
